/*
 * Render the projects in a list format (as opposed to a grid format)
 */
import PropTypes from 'prop-types'
import React from 'react'
import { Box, Flex, FlexRow, Text } from '../components-reusable/index.js'
import { styled } from '../range-theme/index.js'
import Identifier from './Identifier.js'
import ProjectActionsDropdown from './ProjectActionsDropdown.js'

// all tasks = sum of count of tasks in each status
// @sig getTotalTaskCount :: [StatusCount] -> Number
const getTotalTaskCount = statusCounts => statusCounts.reduce((total, statusCount) => total + statusCount.count, 0)

// Adjusted column widths for new layout
const LIST_COL_WIDTHS = {
    identifier: 80,
    nameAndDescription: 400,
    status: 'auto', // will use flexGrow for status
    tasks: 60,
    actions: 40,
}

const StyledStatusPill = styled(Flex, {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: '$default',
    fontWeight: 500,
    fontSize: 14,
    padding: '4px 8px',
    borderRadius: 999,
    gap: 4,
    color: 'white',
    transitionDuration: '0.4s',
    mr: 0,
    background: '$neutral07', // default, override with color prop
    cursor: 'default',
    userSelect: 'none',
    border: 'none',
})

/*
 * Pills showing the task count for each Status
 */
const StatusPillsWithPopover = ({ statusCounts }) => (
    <>
        {statusCounts.map((statusCount, index) => (
            <StyledStatusPill
                key={index}
                css={{
                    background: statusCount.color,
                    opacity: statusCount.count === 0 && 0.3,
                }}
            >
                <Text key={statusCount.id} css={{ fs: 12, color: 'white', fw: 600 }}>
                    {statusCount.name} ({statusCount.count})
                </Text>
            </StyledStatusPill>
        ))}
    </>
)

/*
 * Render the header row of the list view
 */
const ProjectListHeaderRow = () => (
    <FlexRow
        css={{
            alignItems: 'center',
            padding: '8px 8px 8px 16px',
            background: '$neutral09',
            color: '$neutral05',
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: 0.2,
        }}
    >
        <Box css={{ width: LIST_COL_WIDTHS.identifier, minWidth: LIST_COL_WIDTHS.identifier, mr: 16 }} />
        <Text css={{ width: LIST_COL_WIDTHS.nameAndDescription, minWidth: LIST_COL_WIDTHS.nameAndDescription, mr: 16 }}>
            Name
        </Text>
        <Text css={{ width: LIST_COL_WIDTHS.tasks, minWidth: LIST_COL_WIDTHS.tasks, mr: 16 }}>Tasks</Text>
        <Text css={{ flexGrow: 1, mr: 16 }}>Status</Text>
        <Box css={{ width: LIST_COL_WIDTHS.actions, minWidth: LIST_COL_WIDTHS.actions }} />
    </FlexRow>
)

/*
 * Render a single project row in the list view
 * @sig ProjectListRow :: Props -> ReactElement
 * Props = {
 *   project: Project,
 *   projectMetrics: {[projectId]: ProjectMetrics},
 *   onProjectSelected: Project -> void,
 *   projectActionsDropdown: ReactElement?,
 *   showDivider: boolean
 * }
 */
const ProjectListRow = ({ project, projectMetrics, onProjectSelected, projectActionsDropdown, showDivider }) => {
    const onClick = () => onProjectSelected(project)

    const statusCounts = projectMetrics[project.id]?.statusCounts ?? []
    const totalStatusCount = getTotalTaskCount(statusCounts)

    const rowCss = {
        alignItems: 'center',
        padding: '8px 8px 8px 16px',
        background: '$neutral09',
        cursor: 'pointer',
        color: '$neutral04',
        '&:hover': { background: '$neutral08' },
    }
    const identifierColumnCss = { width: LIST_COL_WIDTHS.identifier, minWidth: LIST_COL_WIDTHS.identifier, mr: 16 }
    const nameColumCss = {
        fw: 500,
        fs: '16px',
        color: '$neutral04',
        wordBreak: 'break-word',
        width: LIST_COL_WIDTHS.nameAndDescription,
        minWidth: LIST_COL_WIDTHS.nameAndDescription,
        mr: 16,
    }
    const taskCountColumnCss = {
        fs: 12,
        color: '$neutral04',
        fw: 600,
        width: LIST_COL_WIDTHS.tasks,
        minWidth: LIST_COL_WIDTHS.tasks,
        mr: 16,
    }
    const statusCountsColumnCss = {
        gap: 8,
        flexGrow: 1,
        mr: 16,
        flexWrap: 'wrap',
        maxHeight: 72,
        overflow: 'hidden',
        justifyContent: 'flex-start',
        alignItems: 'center',
        position: 'relative',
    }

    return (
        <div key={project.id}>
            {showDivider && <Box css={{ width: '100%', height: 1, background: '$neutral07' }} />}
            <FlexRow css={rowCss} onClick={onClick}>
                <Identifier projectIdentifier={project.identifier} css={identifierColumnCss} />
                <Text css={nameColumCss}>{project.name}</Text>
                <Text css={taskCountColumnCss}>{totalStatusCount}</Text>
                <Flex css={statusCountsColumnCss}>
                    <StatusPillsWithPopover statusCounts={statusCounts} />
                </Flex>
                {projectActionsDropdown && (
                    <Box css={{ width: LIST_COL_WIDTHS.actions, minWidth: LIST_COL_WIDTHS.actions }}>
                        {projectActionsDropdown}
                    </Box>
                )}
            </FlexRow>
        </div>
    )
}

/*
 * Render the projects in a list format
 * @sig ProjectListView :: Props -> ReactElement
 */
const ProjectListView = ({
    canManageProject,
    searchPhrase,
    projects,
    projectMetrics,
    onProjectSelected,
    onHideProjectClick,
    onShowProjectClick,
    onDeleteProjectClick,
}) => {
    const renderProjectRow = (project, idx) => {
        const projectActionsDropdown = canManageProject ? (
            <ProjectActionsDropdown
                isArchived={project.isArchived}
                canDelete={canManageProject}
                onShowProject={() => onShowProjectClick(project)}
                onHideProject={() => onHideProjectClick(project)}
                onDeleteProject={() => onDeleteProjectClick(project)}
            />
        ) : null

        return (
            <ProjectListRow
                key={project.id}
                project={project}
                projectMetrics={projectMetrics}
                onProjectSelected={onProjectSelected}
                projectActionsDropdown={projectActionsDropdown}
                showDivider={idx !== 0}
            />
        )
    }

    const matchesSearch = project =>
        !searchPhrase ||
        project.name.toLowerCase().includes(searchPhrase) ||
        project.address.toLowerCase().includes(searchPhrase)

    // Sort projects by name A-Z and filter by search text
    const sortedProjects = [...projects].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    const filteredProjects = sortedProjects.filter(matchesSearch)

    return (
        <Box
            css={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                background: '$neutral09',
                borderRadius: 8,
                boxShadow: '0px 0px 0px #5D81FF50',
                border: '1px solid $neutral07',
            }}
        >
            <ProjectListHeaderRow />
            <Box css={{ width: '100%', height: 1, background: '$neutral07' }} />
            <Flex direction="column" css={{ width: '100%' }}>
                {filteredProjects.map(renderProjectRow)}
            </Flex>
        </Box>
    )
}

const ProjectShape = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    identifier: PropTypes.string,
    isArchived: PropTypes.bool,
}

const StatusCountShape = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
}

const ProjectMetricsShape = {
    statusCounts: PropTypes.arrayOf(PropTypes.shape(StatusCountShape)),
}

const ProjectListViewShape = {
    canManageProject: PropTypes.bool.isRequired,
    searchPhrase: PropTypes.string,
    projects: PropTypes.arrayOf(PropTypes.shape(ProjectShape)).isRequired,
    projectMetrics: PropTypes.objectOf(PropTypes.shape(ProjectMetricsShape)).isRequired,
    onProjectSelected: PropTypes.func.isRequired,
    onHideProjectClick: PropTypes.func.isRequired,
    onShowProjectClick: PropTypes.func.isRequired,
    onDeleteProjectClick: PropTypes.func.isRequired,
}

ProjectListView.propTypes = ProjectListViewShape

export { getTotalTaskCount, ProjectListView }
